// LRPO-5898 — SAVE-ON-EXIT. The old Exit flow opened a "Discard Changes"
// modal whenever an edit had not autosaved yet. The modal could only ever
// discard the last few seconds of typing (the autosave debounce window)
// while claiming "all your modifications will be lost" — and every one of
// the five prior QA rounds on this surface was that modal firing on a
// phantom dirty state. Exit now FLUSHES the pending edit through the
// autosave controller's own save path and leaves; a dialog appears ONLY if
// that save fails.
//
// ⚠ Navigation keys off the autosave lifecycle EVENTS, never off
// window.editFormUpdated going false: the save response's own id write-back
// re-dirties the form on the same tick (see territory_autosave_controller),
// so polling the flag can hang or loop. If the flag is still true when a
// save SUCCEEDS, a genuine edit arrived mid-flight — flush again and keep
// waiting; each pass posts real pending work, so the loop terminates.

import { Controller } from "@hotwired/stimulus"

// Give a slow save a real chance before declaring failure — but never leave
// the button spinning forever if no lifecycle event ever arrives.
const FLUSH_TIMEOUT_MS = 8000

export default class extends Controller {
  static values = {
    indexUrl: String,
    failModalId: { type: String, default: "exit-save-failed-modal" }
  }

  connect() {
    // The failure dialog's buttons live outside this controller's element,
    // so they are wired by attribute, delegated on the document.
    this._onDocClick = (event) => {
      if (event.target.closest("[data-territory-exit-leave]")) {
        this._navigate()
      } else if (event.target.closest("[data-territory-exit-retry]")) {
        this._hideFailModal()
        this._beginFlush()
      }
    }
    document.addEventListener("click", this._onDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick)
    this._teardown()
  }

  exit(event) {
    event.preventDefault()

    if (!window.editFormUpdated) {
      this._navigate()
      return
    }
    if (this._exiting) return
    this._beginFlush()
  }

  _beginFlush() {
    this._exiting = true
    this._showSaving()

    this._onSaved = (event) => {
      // Only the FORM autosave settles an exit — the rename modal's
      // controller shares these event names with source:'name'.
      if (!event || !event.detail || event.detail.source !== "form") return
      if (event.detail.settled === false) {
        // More work remains (an edit landed mid-flight or sits queued behind
        // the request). The autosave controller itself fires the follow-up
        // save — immediately, because our flush marked it so — and that save
        // emits its own settled event. Just keep waiting.
        return
      }
      this._teardown()
      this._navigate()
    }
    this._onFailed = (event) => {
      if (!event || !event.detail || event.detail.source !== "form") return
      this._teardown()
      this._showFailModal()
    }
    document.addEventListener("territory:autosaved", this._onSaved)
    document.addEventListener("territory:autosave-failed", this._onFailed)
    this._timeout = setTimeout(() => {
      this._teardown()
      this._showFailModal()
    }, FLUSH_TIMEOUT_MS)

    document.dispatchEvent(new CustomEvent("territory:flush-request"))
  }

  _teardown() {
    if (this._onSaved) document.removeEventListener("territory:autosaved", this._onSaved)
    if (this._onFailed) document.removeEventListener("territory:autosave-failed", this._onFailed)
    this._onSaved = null
    this._onFailed = null
    if (this._timeout) clearTimeout(this._timeout)
    this._timeout = null
    this._exiting = false
    this._restoreButton()
  }

  _showSaving() {
    const btn = this.element
    if (!(btn instanceof HTMLElement)) return
    if (this._originalHtml == null) this._originalHtml = btn.innerHTML
    btn.setAttribute("disabled", "disabled")
    btn.disabled = true
    // textContent, not innerHTML with variables — nothing user-controlled here,
    // but keep the swap inert anyway.
    btn.textContent = "Saving…"
    btn.setAttribute("aria-busy", "true")
  }

  _restoreButton() {
    const btn = this.element
    if (!(btn instanceof HTMLElement)) return
    if (this._originalHtml != null) btn.innerHTML = this._originalHtml
    btn.removeAttribute("disabled")
    btn.disabled = false
    btn.removeAttribute("aria-busy")
  }

  _showFailModal() {
    const el = document.getElementById(this.failModalIdValue)
    // Never trap the user: if the dialog cannot open, the edit is already
    // lost-or-saved either way — leave, matching the old fallbacks.
    if (!el || typeof window.bootstrap === "undefined" || !window.bootstrap.Modal) {
      this._navigate()
      return
    }
    window.bootstrap.Modal.getOrCreateInstance(el).show()
  }

  _hideFailModal() {
    const el = document.getElementById(this.failModalIdValue)
    if (el && typeof window.bootstrap !== "undefined" && window.bootstrap.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(el).hide()
    }
  }

  _navigate() {
    window.location.href = this.indexUrlValue
  }
}
